// @flow

import * as React from 'react';
import cx from 'classnames';
import styles from './VenueInfo.scss';
import type { PreviewVenue, HighlightCapacityType } from './types';
import Rating from 'common/components/Rating';
import VenueLink from './VenueLink';

export type Props = {|
  venue: PreviewVenue,
  onClickVenueLink?: () => void,
  openInNewTab: boolean,
  highlightCapacity?: HighlightCapacityType,
  isActive?: boolean
|};

export default function VenueInfo(props: Props) {
  const {
    venue,
    onClickVenueLink,
    openInNewTab,
    highlightCapacity = 'BOTH',
    isActive = false
  } = props;

  return (
    <VenueLink
      venue={venue}
      openInNewTab={openInNewTab}
      onClickVenueLink={onClickVenueLink}
    >
      <VenueInfoContainer>
        <VenueTitle venue={venue} isActive={isActive} />
        <VenueDetailsLeft venue={venue} />
        <VenueDetailsRight
          venue={venue}
          highlightCapacity={highlightCapacity}
        />
      </VenueInfoContainer>
    </VenueLink>
  );
}

export function VenueInfoContainer(props: {|
  className?: string,
  children: React.Node
|}) {
  return (
    <div className={cx(styles.infoContainer, props.className)}>
      {props.children}
    </div>
  );
}

export function VenueTitle(props: {|
  venue: PreviewVenue,
  isActive: boolean,
  warnings?: React.Node
|}) {
  return (
    <div
      className={cx(styles.venueTitle, {
        [styles['venueTitle--active']]: props.isActive
      })}
    >
      {props.warnings}
      {props.venue.title}
    </div>
  );
}

export function VenueDetailsLeft(props: {|
  venue: PreviewVenue,
  additionalItem?: React.Node
|}) {
  return (
    <span className={styles.venueDetailsLeft}>
      <span className={styles.venueDetailsItem}>
        <Rating score={props.venue.reviews_average} />
      </span>
      {props.additionalItem && (
        <span className={styles.venueDetailsItem}>{props.additionalItem}</span>
      )}
    </span>
  );
}

export function VenueDetailsRight(props: {|
  venue: PreviewVenue,
  highlightCapacity: HighlightCapacityType
|}) {
  const { venue, highlightCapacity } = props;
  return (
    <span className={styles.venueDetailsRight}>
      <AvailabilityData hasAvailabilityData={venue.has_availability_data} />
      <CapacitySittingData
        capacity={venue.capacity_sitting}
        highlight={['SITTING', 'BOTH'].includes(highlightCapacity)}
      />
      <CapacityStandingData
        capacity={venue.capacity_standing}
        highlight={['STANDING', 'BOTH'].includes(highlightCapacity)}
      />
    </span>
  );
}

function AvailabilityData({ hasAvailabilityData }) {
  if (!hasAvailabilityData) return null;

  return (
    <span
      className={styles.venueDetailsWithSeparator}
      title={__('venues.similar_venues.has_availability_data')}
    >
      <span className={styles.venueDetailsItem}>
        <i className="fa fa-calendar-check-o" />
      </span>
    </span>
  );
}

type CapacityProps = {| capacity: number | null, highlight: boolean |};

function CapacitySittingData({ capacity, highlight }: CapacityProps) {
  return (
    <span
      className={cx(styles.venueDetailsWithSeparator, {
        [styles.muted]: !highlight
      })}
      title={__('venues.similar_venues.capacity_sitting')}
    >
      <span className={styles.venueDetailsItem}>
        <i className="m-icon-sitting" />
        &nbsp;
        {capacity}
      </span>
    </span>
  );
}

function CapacityStandingData({ capacity, highlight }: CapacityProps) {
  return (
    <span
      className={cx(styles.venueDetailsWithSeparator, {
        [styles.muted]: !highlight
      })}
      title={__('venues.similar_venues.capacity_standing')}
    >
      <span className={styles.venueDetailsItem}>
        <i className="fa fa-male" />
        &nbsp;
        {capacity}
      </span>
    </span>
  );
}
